import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ReportsService } from '../../reports.service';
import { appColors } from '../../../core/styles/colors';
import { EChartsOption, EChartsType, SeriesOption } from 'echarts';
import { DateTime, Duration } from 'luxon';
import { DateUtils } from '../../../core/utilities/date-time-helpers';
import CartesianAxisModel from 'echarts/types/src/coord/cartesian/AxisModel';
import GlobalModel from 'echarts/types/src/model/Global';
import TimeScale from 'echarts/types/src/scale/Time';
import { MatDialog } from '@angular/material/dialog';
import {
  chartDefaultOptions,
  chartExpandIcon,
  getWeekendsArea,
  zoomToTimeSeriesPoint,
} from '../../../core/styles/chart-defauls';
import { NgxEchartsDirective } from 'ngx-echarts';
import { MatActivityDashboardZoomDialogComponent } from './mat-activity-dashboard-zoom-dialog.component';

@Component({
  selector: 'app-activity-chart',
  templateUrl: './activity-chart.component.html',
  standalone: true,
  imports: [NgxEchartsDirective],
})
export class ActivityChartComponent implements OnInit, OnChanges {
  @Input() showExpand = false;
  @Input() startDate: string;
  @Input() endDate: string;
  @Input() data: any[] = [];
  @Input() chartType: 'line' | 'bar' = 'line';

  @ViewChild('expandBtn') expandBtn;

  colors = appColors;
  groupBy = 'day';
  updateOptions: EChartsOption = {};
  initOptions = {};

  options: EChartsOption = {
    title: Object.assign({}, chartDefaultOptions.title, {
      text: 'Call Summary By Time',
    }),
    legend: Object.assign({}, chartDefaultOptions.legend),
    grid: Object.assign({}, chartDefaultOptions.grid),
    dataset: [
      {
        dimensions: [
          { name: 'date', type: 'time' },
          { name: 'isIncoming', type: 'number' },
          { name: 'isOutgoing', type: 'number' },
          { name: 'isMissed', type: 'number' },
          { name: 'isMailbox', type: 'number' },
        ],
        source: [],
      },
    ],
    toolbox: [
      {
        show: true,
        feature: {
          dataZoom: {
            yAxisIndex: 'none',
          },
          magicType: {
            type: ['line', 'bar'],
          },
          saveAsImage: {},
          myZoom: {
            show: this.showExpand,
            title: 'Expand',
            icon: chartExpandIcon,
            onclick: () => {
              console.log('on expand click');
              this.expandBtn.nativeElement.click();
            },
          },
        },
      },
    ],
    xAxis: {
      type: 'time',
      boundaryGap: ['20%', '20%'],
    },
    yAxis: {
      type: 'value',
      scale: true,
      min: 0,
      axisLine: {
        show: true,
      },
      name: 'Total Count',
      nameGap: 30,
      nameRotate: 90,
      nameLocation: 'middle',
    },
    dataZoom: [
      {
        type: 'inside',
        filterMode: 'filter',
        minValueSpan: 600 * 24 * 1000,
        start: 0,
        end: 100,
      },
      {
        start: 0,
        end: 100,
        labelFormatter: function (value: number) {
          const dateTime = DateTime.fromMillis(value);
          return dateTime.toFormat('MM/dd/yyyy');
        },
      },
    ],
    series: [
      {
        type: 'line',
        smooth: true,
        // stack: 'calls',
        sampling: 'sum',
        showSymbol: false,
        smoothMonotone: 'x',
        name: 'Incoming Calls',
        color: appColors.primaryGreen,
        encode: {
          x: ['date'],
          y: ['isIncoming'],
          itemName: ['isIncoming'],
        },
      },
      {
        type: 'line',
        smooth: true,
        // stack: 'calls',
        sampling: 'sum',
        name: 'Outgoing Calls',
        showSymbol: false,
        smoothMonotone: 'x',
        color: appColors.primaryBlue,
        encode: {
          x: ['date'],
          y: ['isOutgoing'],
          itemName: ['isOutgoing'],
        },
      },
      {
        type: 'line',
        smooth: true,
        // stack: 'calls',
        sampling: 'sum',
        name: 'Missed Calls',
        showSymbol: false,
        smoothMonotone: 'x',
        color: appColors.primaryRed,
        encode: {
          y: ['isMissed'],
          x: ['date'],
          itemName: ['isMissed'],
        },
      },
      {
        type: 'line',
        smooth: true,
        // stack: 'calls',
        sampling: 'sum',
        name: 'Mailbox Calls',
        showSymbol: false,
        smoothMonotone: 'x',
        color: appColors.teal,
        encode: {
          y: ['isMailbox'],
          x: ['date'],
          itemName: ['isMailbox'],
        },
      },
    ],
    tooltip: {
      show: true,
      trigger: 'axis',
      axisPointer: {
        label: {
          formatter: params => {
            const dateTime = DateUtils.localDateToDateTime(new Date(params.value as number));
            // const dateTime = DateTime.fromMillis(params.value as number);
            if (this.groupBy === 'day') {
              return dateTime.toFormat('MM/dd/yyyy EEE');
            }
            return dateTime.toFormat('MM/dd/yyyy EEE hh:mm a');
          },
        },
      },
    },
  };

  private chart: any;

  constructor(
    public reportsService: ReportsService,
    public dialog: MatDialog,
  ) {
    const resizeObserver = new ResizeObserver(() => {
      this.resizeChart();
    });
    resizeObserver.observe(document.body);
  }

  ngOnInit() {
    console.log('my on init');
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('my on ngOnChanges');

    this.updateSeries();

    this.data.sort((a, b) => {
      const aTime = new Date(a.date);
      const bTime = new Date(b.date);
      return aTime > bTime ? -1 : aTime < bTime ? 1 : 0;
    });

    this.updateChart();
  }

  resizeChart() {
    if (this.chart) {
      this.chart.resize();
    }
  }

  onChartInit($event: any) {
    this.chart = $event;
    console.log('on chart init:', $event);
  }

  getECModel(chart: EChartsType): GlobalModel {
    // @ts-ignore
    return chart.getModel();
  }

  getNewGroupBy() {
    if (!this.chart) {
      return 'day';
    }

    const xAxis = this.getECModel(this.chart).getComponent('xAxis', 0) as CartesianAxisModel;
    const scale: TimeScale = xAxis.axis.scale as TimeScale;
    const duration = Duration.fromMillis(scale._approxInterval);

    if (duration.as('hours') > 18) {
      return 'day';
    } else {
      return 'hour';
    }
  }

  updateSeries() {
    console.log('update series');
    for (const item of this.options.series as SeriesOption[]) {
      item.type = this.chartType;
      item.universalTransition = true;

      if (item.type === 'bar') {
        item.stack = 'calls';
        item.barMaxWidth = 50;
        this.options.dataZoom![0].filterMode = 'filter';
      } else if (item.type === 'line') {
        item.stack = undefined;
        item.smooth = false;
        item.showSymbol = false;
        this.options.dataZoom![0].filterMode = 'none';
      }
    }
  }

  updateChart() {
    this.groupBy = this.getNewGroupBy();

    const groupedData = this.groupByAndSumAll(this.groupBy, this.data);

    const dataset = Object.assign({}, this.options.dataset![0], {
      source: groupedData.map(item => {
        return {
          date: item.date, // DateUtils.localDateFromDateTime(DateTime.fromJSDate(item.date)),
          isIncoming: item.isIncoming,
          isOutgoing: item.isOutgoing,
          isMissed: item.isMissed,
          isMailbox: item.isMailbox,
        };
      }),
    });
    this.options.dataset![0] = dataset;

    const currentOptions = this.chart ? this.chart.getOption() : this.options.dataZoom;
    if (this.chart) {
      currentOptions.dataZoom[0].filterMode = this.options.dataZoom[0].filterMode;
    }

    this.options.toolbox![0].feature.magicType.iconStatus = {};
    this.options.toolbox![0].feature.magicType.iconStatus[this.chartType] = 'emphasis';
    this.options.toolbox![0].feature.myZoom.show = this.showExpand;

    const startRange = DateTime.fromISO(this.startDate).plus({ day: -1 }).toJSDate();
    const endRange = DateTime.fromISO(this.endDate).plus({ day: 1 }).toJSDate();
    const markArea = getWeekendsArea(startRange, endRange);
    for (const series of this.options.series as Array<SeriesOption>) {
      series.markArea = markArea;
    }

    this.updateOptions = {
      toolbox: this.options.toolbox,
      series: this.options.series,
      dataset: dataset,
      dataZoom: currentOptions.dataZoom,
      xAxis: {
        min: startRange,
        max: endRange,
      },
    };
  }

  groupByAndSumAll(groupBy: string, dataArr: any[]): any[] {
    const result: any[] = [];
    dataArr.reduce(function (res, value) {
      let key: string = DateUtils.localDateFromDateTime(DateTime.fromJSDate(value.date)).toJSON(); // value.date.toJSON();
      if (groupBy === 'day') {
        const dateKey = DateUtils.localDateFromDateTime(DateTime.fromJSDate(value.date)); // new Date(value.date.getTime());
        dateKey.setHours(0);
        dateKey.setMinutes(0);
        dateKey.setSeconds(0);

        key = dateKey.toJSON();
      } else if (groupBy === 'hour') {
        const dateKey = DateUtils.localDateFromDateTime(DateTime.fromJSDate(value.date)); // new Date(value.date.getTime());
        dateKey.setMinutes(0);
        dateKey.setSeconds(0);

        key = dateKey.toJSON();
      }
      console.log(key);
      if (!res[key]) {
        res[key] = {
          date: groupBy === 'day' ? new Date(key) : DateUtils.localDateFromDateTime(DateTime.fromISO(key)),
          isIncoming: 0,
          isOutgoing: 0,
          isMissed: 0,
          isMailbox: 0,
        };
        result.push(res[key]);
      }
      if (value.isIncoming) {
        res[key].isIncoming += value.isIncoming;
      }
      if (value.isOutgoing) {
        res[key].isOutgoing += value.isOutgoing;
      }
      if (value.isMissed) {
        res[key].isMissed += value.isMissed;
      }
      if (value.isMailbox) {
        res[key].isMailbox += value.isMailbox;
      }

      return res;
    }, {});

    return result;
  }

  onChartDataZoom($event: unknown) {
    const newGroup = this.getNewGroupBy();

    if (newGroup !== this.groupBy) {
      this.updateChart();
    }
  }

  onChartMagicTypeChanged($event: any) {
    console.log($event);
    if ($event.currentType) {
      this.chartType = $event.currentType;
      this.updateSeries();
      this.updateChart();
    }
  }

  onExpandClick() {
    this.dialog.open(MatActivityDashboardZoomDialogComponent, {
      width: '90%',
      height: '90%',
      data: {
        chartData: this.data,
        dialogTitle: 'Call Summary By Time',
        showActivityChart: true,
        chartType: this.chartType,
        startDate: this.startDate,
        endDate: this.endDate,
      },
    });
  }

  onChartClick($event: any) {
    console.log($event);
    zoomToTimeSeriesPoint($event, this.chart);
  }
}
